import { useCallback, useMemo, useState } from 'react';

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 25;
const DEFAULT_DIRECTION = 'asc';

const isMeaningfulValue = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim() !== '';
  return true;
};

const normalizeDirection = (direction) => (String(direction || '').toLowerCase() === 'desc' ? 'desc' : 'asc');

export const useListView = ({
  initialFilters = {},
  initialSort = { field: '', direction: DEFAULT_DIRECTION },
  initialPagination = { page: DEFAULT_PAGE, limit: DEFAULT_LIMIT },
  sortQueryKeys = { field: 'sortBy', direction: 'sortOrder' },
} = {}) => {
  const [filters, setFilters] = useState(initialFilters);
  const [sort, setSortState] = useState({
    field: initialSort?.field || '',
    direction: normalizeDirection(initialSort?.direction || DEFAULT_DIRECTION),
  });
  const [pagination, setPagination] = useState({
    page: Number(initialPagination?.page) || DEFAULT_PAGE,
    limit: Number(initialPagination?.limit) || DEFAULT_LIMIT,
    total: Number(initialPagination?.total) || 0,
    pages: Number(initialPagination?.pages) || 1,
  });

  const setFilter = useCallback((key, value, { resetPage = true } = {}) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    if (resetPage) {
      setPagination((prev) => ({ ...prev, page: DEFAULT_PAGE }));
    }
  }, []);

  const setManyFilters = useCallback((nextFilters, { resetPage = true } = {}) => {
    setFilters((prev) => ({ ...prev, ...nextFilters }));
    if (resetPage) {
      setPagination((prev) => ({ ...prev, page: DEFAULT_PAGE }));
    }
  }, []);

  const setSort = useCallback((field, direction) => {
    if (!field) return;
    setSortState((prev) => {
      if (prev.field !== field) {
        return { field, direction: normalizeDirection(direction || DEFAULT_DIRECTION) };
      }
      if (direction) {
        return { field, direction: normalizeDirection(direction) };
      }
      return { field, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
    });
    setPagination((prev) => ({ ...prev, page: DEFAULT_PAGE }));
  }, []);

  const setPage = useCallback((nextPage) => {
    const target = Number(nextPage);
    if (!Number.isFinite(target)) return;
    setPagination((prev) => ({ ...prev, page: Math.max(DEFAULT_PAGE, Math.trunc(target)) }));
  }, []);

  const setLimit = useCallback((nextLimit) => {
    const target = Number(nextLimit);
    if (!Number.isFinite(target) || target <= 0) return;
    setPagination((prev) => ({ ...prev, limit: Math.trunc(target), page: DEFAULT_PAGE }));
  }, []);

  const applyServerPagination = useCallback((serverPagination = {}) => {
    setPagination((prev) => ({
      ...prev,
      total: Number(serverPagination.total) || 0,
      pages: Number(serverPagination.pages) || 1,
      page: Number(serverPagination.page) || prev.page || DEFAULT_PAGE,
      limit: Number(serverPagination.limit) || prev.limit || DEFAULT_LIMIT,
    }));
  }, []);

  const resetListView = useCallback(() => {
    setFilters(initialFilters);
    setSortState({
      field: initialSort?.field || '',
      direction: normalizeDirection(initialSort?.direction || DEFAULT_DIRECTION),
    });
    setPagination({
      page: Number(initialPagination?.page) || DEFAULT_PAGE,
      limit: Number(initialPagination?.limit) || DEFAULT_LIMIT,
      total: 0,
      pages: 1,
    });
  }, [initialFilters, initialPagination, initialSort]);

  const query = useMemo(() => {
    const params = {};
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (isMeaningfulValue(value)) {
        params[key] = typeof value === 'string' ? value.trim() : value;
      }
    });
    params.page = pagination.page || DEFAULT_PAGE;
    params.limit = pagination.limit || DEFAULT_LIMIT;
    if (sort.field) {
      params[sortQueryKeys.field || 'sortBy'] = sort.field;
      params[sortQueryKeys.direction || 'sortOrder'] = sort.direction;
    }
    return params;
  }, [filters, pagination.limit, pagination.page, sort.direction, sort.field, sortQueryKeys.direction, sortQueryKeys.field]);

  return {
    filters,
    sort,
    pagination,
    query,
    setFilter,
    setManyFilters,
    setSort,
    setPage,
    setLimit,
    applyServerPagination,
    resetListView,
  };
};

export default useListView;
